import React, { useEffect, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';

const TermsOfUse: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useI18n();
  const [browserLanguage, setBrowserLanguage] = useState<string>('en');

  // Detectar idioma do navegador para usuários não logados
  useEffect(() => {
    const detectBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.languages[0];
      const isPortuguese = browserLang.startsWith('pt');
      const detectedLang = isPortuguese ? 'pt' : 'en';
      setBrowserLanguage(detectedLang);
      
      // Se não há usuário logado, usar idioma do navegador
      if (!localStorage.getItem('user')) {
        changeLanguage(detectedLang as 'pt' | 'en');
      }
    };

    detectBrowserLanguage();
  }, [changeLanguage]);

  const currentLang = currentLanguage || browserLanguage;

  return (
    <div className="min-h-screen bg-black">
      <TopNavigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8 mt-8">
        <div className="bg-gray-900 rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-white mb-8">
            {currentLang === 'pt' ? 'Termos de Uso' : 'Terms of Use'}
          </h1>
          
          <div className="prose prose-lg max-w-none">
            {currentLang === 'pt' ? (
              <div>
                <h2 className="text-2xl font-semibold text-gray-200 mb-4">
                  Termos de Uso - sOSSiety
                </h2>
                
                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    1. Aceitação dos Termos
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Ao utilizar a plataforma sOSSiety, você concorda com estes termos de uso. 
                    Se você não concorda com qualquer parte destes termos, não deve utilizar nossa plataforma.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    2. Uso da Plataforma
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    A plataforma sOSSiety é destinada à comunidade de Jiu-Jitsu, permitindo que 
                    praticantes e instrutores se conectem, compartilhem experiências e organizem treinos.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    3. Responsabilidades do Usuário
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Você é responsável por manter a confidencialidade de sua conta e por todas 
                    as atividades que ocorrem sob sua conta. Você concorda em notificar-nos 
                    imediatamente sobre qualquer uso não autorizado de sua conta.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    4. Conteúdo do Usuário
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Você mantém todos os direitos sobre o conteúdo que publica na plataforma. 
                    Ao publicar conteúdo, você nos concede uma licença não exclusiva para 
                    usar, modificar e exibir esse conteúdo na plataforma.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    5. Limitação de Responsabilidade
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    A plataforma sOSSiety é fornecida "como está" sem garantias de qualquer tipo. 
                    Não nos responsabilizamos por danos diretos, indiretos, incidentais ou 
                    consequenciais resultantes do uso da plataforma.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    6. Modificações dos Termos
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Reservamo-nos o direito de modificar estes termos a qualquer momento. 
                    As modificações entrarão em vigor imediatamente após a publicação. 
                    O uso continuado da plataforma constitui aceitação dos termos modificados.
                  </p>
                </section>

                <div className="mt-12 p-6 bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-400">
                    <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR')}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>Este é um conteúdo placeholder. Os termos oficiais serão adicionados posteriormente.</em>
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-semibold text-gray-200 mb-4">
                  Terms of Use - sOSSiety
                </h2>
                
                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    1. Acceptance of Terms
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    By using the sOSSiety platform, you agree to these terms of use. 
                    If you do not agree with any part of these terms, you should not use our platform.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    2. Use of Platform
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    The sOSSiety platform is intended for the Jiu-Jitsu community, allowing 
                    practitioners and instructors to connect, share experiences, and organize training sessions.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    3. User Responsibilities
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    You are responsible for maintaining the confidentiality of your account and for all 
                    activities that occur under your account. You agree to notify us immediately 
                    of any unauthorized use of your account.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    4. User Content
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    You retain all rights to the content you publish on the platform. 
                    By publishing content, you grant us a non-exclusive license to 
                    use, modify, and display that content on the platform.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    5. Limitation of Liability
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    The sOSSiety platform is provided "as is" without warranties of any kind. 
                    We are not liable for direct, indirect, incidental, or consequential damages 
                    resulting from the use of the platform.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    6. Terms Modifications
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We reserve the right to modify these terms at any time. 
                    Modifications will take effect immediately after publication. 
                    Continued use of the platform constitutes acceptance of the modified terms.
                  </p>
                </section>

                <div className="mt-12 p-6 bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-400">
                    <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US')}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>This is placeholder content. Official terms will be added later.</em>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;
